import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateVisitModal from '/@/components/field-list-v2/components/sidebar/components/create-visit-modal';
import { useHasWriteAccess, useIsValidatedConsultant, useIsValidatedSquad } from '/@/hooks/session';
import { _ } from '/@/locale';
import { reverse } from '/@/utils/router';
import type { GrowersTabType } from '../../..';
import { agendVisit } from '../../../grower/components/next-visit-modal';
import CreateRecommendationModal from '../../../rv/components/create-recommendation-modal';
import DeleteGrowerModal from '../delete-grower-modal';
import EditGrowerModal from '../edit-grower-modal';
import NewFieldGrowersModal from '../new-field-modal';

const GrowersListActions = ({
    growerNodeId,
    editable,
    growerPhone,
    growerName,
    tab,
    onGrowerDeleteSuccess,
    deletable,
    isInDetailPage = false,
}: {
    growerNodeId: number;
    editable: boolean;
    growerPhone: string;
    growerName: string;
    tab: GrowersTabType;
    onGrowerDeleteSuccess: (growerName: string) => void;
    deletable: boolean;
    isInDetailPage: boolean;
}) => {
    const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState<HTMLElement | null>(null);
    const navigate = useNavigate();
    const [openDeleteGrowerModal, setOpenDeleteGrowerModal] = useState(false);
    const [recommendationsModalOpened, setRecommendationsModalOpened] = useState(false);
    const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);
    const [openEditGrowerModal, setOpenEditGrowerModal] = useState(false);
    const [createVisitModalOpened, setCreateVisitModalOpened] = useState(false);
    const isValidatedSquad = useIsValidatedSquad();
    const isValidatedConsultant = useIsValidatedConsultant();
    const hasWriteAccess = useHasWriteAccess();
    // Consultants may edit a grower's info (the edit modal has no parent selector,
    // so they can't change RV/Advisor parents), but they cannot delete growers.
    const canEditGrower = hasWriteAccess;
    const canDeleteGrower = hasWriteAccess && !isValidatedConsultant;

    const menuItems: {
        label: string;
        onClick: (e: React.MouseEvent<HTMLElement>) => void;
        disabled?: boolean;
        hidden?: boolean;
    }[] = useMemo(() => {
        if (tab === 'current-campaign')
            return [
                {
                    label: _('Generar recomendación'),
                    onClick: () => {
                        setRecommendationsModalOpened(true);
                    },
                    hidden: isValidatedSquad,
                },
                {
                    label: _('Crear informe de visita'),
                    onClick: () => {
                        setCreateVisitModalOpened(true);
                    },
                    hidden: isValidatedSquad,
                },
                {
                    label: _('Agregar un lote'),
                    onClick: () => {
                        setNewFieldModalOpen(true);
                    },
                    hidden: !hasWriteAccess,
                },
                {
                    label: _('Agendar una visita'),
                    onClick: () => {
                        agendVisit(growerName, growerPhone);
                    },
                    hidden: !hasWriteAccess,
                },
                {
                    label: _('Ir al detalle del productor'),
                    onClick: () => {
                        navigate(reverse('Growers.detail', { params: { growerNodeId: growerNodeId }, qs: { tab } }));
                    },
                    hidden: isInDetailPage,
                },
                {
                    label: _('Editar información'),
                    disabled: !editable,
                    onClick: () => {
                        setOpenEditGrowerModal(true);
                    },
                    hidden: !canEditGrower,
                },
                {
                    label: _('Eliminar productor'),
                    disabled: !deletable,
                    hidden: !canDeleteGrower,
                    onClick: async () => {
                        setOpenDeleteGrowerModal(true);
                    },
                },
            ];

        if (tab === 'past-reports')
            return [
                {
                    label: _('Ver reportes del productor'),
                    onClick: () => {
                        navigate(reverse('Growers.detail', { params: { growerNodeId: growerNodeId }, qs: { tab } }));
                    },
                },
            ];
        if (tab === 'recommendations')
            return [
                {
                    label: _('Ver recomendaciones'),
                    onClick: () => {
                        navigate(reverse('Growers.detail', { params: { growerNodeId: growerNodeId }, qs: { tab } }));
                    },
                },
                {
                    label: _('Generar recomendación'),
                    hidden: isValidatedSquad,
                    onClick: () => {
                        setRecommendationsModalOpened(true);
                    },
                },
            ];
        return [];
    }, [
        tab,
        editable,
        growerNodeId,
        growerName,
        growerPhone,
        navigate,
        deletable,
        isValidatedSquad,
        hasWriteAccess,
        canEditGrower,
        canDeleteGrower,
        isInDetailPage,
    ]);

    return (
        <Box>
            <IconButton
                size='small'
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    setRowActionsAnchorEl(e.currentTarget);
                }}
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                open={!!rowActionsAnchorEl}
                onClose={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    setRowActionsAnchorEl(null);
                }}
                anchorEl={rowActionsAnchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                }}
            >
                {menuItems
                    .filter((item) => !item.hidden)
                    .map((item) => (
                        <MenuItem
                            key={item.label}
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                                e.stopPropagation();
                                item.onClick(e);
                                setRowActionsAnchorEl(null);
                            }}
                            disabled={item.disabled}
                        >
                            {item.label}
                        </MenuItem>
                    ))}
            </Menu>
            {createVisitModalOpened && (
                <CreateVisitModal
                    open={createVisitModalOpened}
                    setOpen={setCreateVisitModalOpened}
                    userNodeId={growerNodeId}
                />
            )}
            <DeleteGrowerModal
                growerNodeId={growerNodeId}
                onSuccess={onGrowerDeleteSuccess}
                open={openDeleteGrowerModal}
                setOpen={setOpenDeleteGrowerModal}
            />
            <CreateRecommendationModal
                open={recommendationsModalOpened}
                setOpen={setRecommendationsModalOpened}
                userNodeId={growerNodeId}
                entrySource='growers-list'
            />
            <NewFieldGrowersModal
                open={newFieldModalOpen}
                setOpen={setNewFieldModalOpen}
                rvUserNodeId={undefined}
                growerUserNodeId={growerNodeId}
                trackEntrySource='growers-list-dashboard'
            />
            <EditGrowerModal
                open={openEditGrowerModal}
                onClose={() => setOpenEditGrowerModal(false)}
                growerNodeId={growerNodeId}
            />
        </Box>
    );
};

export default GrowersListActions;
